// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiCheckCircle, FiSearch } from "react-icons/fi";
import NoctisStatusDot from "../../components/noctis/NoctisStatusDot";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import { integrationApi } from "../../api/apiClient";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("nl-NL");
};

const statusLabel = (status, t) => {
  const normalized = (status || "").toUpperCase();
  if (!normalized) return "—";
  const map = {
    EXECUTED: t("noctis.status.executed", { defaultValue: "Uitgevoerd" }),
    SCHEDULED: t("noctis.status.scheduled", { defaultValue: "Gepland" }),
    REQUESTED: t("noctis.status.requested", { defaultValue: "Ingediend" }),
    READY_FOR_DELETE: t("noctis.status.ready_for_delete", { defaultValue: "Gereed voor verwijdering" }),
    SCHEDULED_FOR_DELETE: t("noctis.status.scheduled_for_delete", { defaultValue: "Gepland voor verwijdering" }),
    MARKED_FOR_DELETE: t("noctis.status.marked_for_delete", { defaultValue: "Gemarkeerd voor verwijdering" }),
  };
  return map[normalized] || status;
};

const NoctisReferenceStatus = () => {
  const { t } = useTranslation();
  const [referenceId, setReferenceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const hasResult = useMemo(() => Boolean(result), [result]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = referenceId.trim();
    if (!trimmed) {
      setError(t("noctis.status.validation_required", { defaultValue: "Vul een referentie in." }));
      setResult(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      const params = new URLSearchParams({ referenceId: trimmed });
      const backendEnv = (window && window.__ENV__ && window.__ENV__.BACKEND_URL) || "";
      // Default naar de echte backend (8443) als er geen env is gezet, en strip een eventuele /api/v1 suffix
      const baseForQuery = (backendEnv || "http://localhost:8443/api/v1").replace(/\/+$/, "");
      const resolvedBase = baseForQuery.replace(/\/api\/v1$/, "");
      const url = `${resolvedBase}/api/public/avg-status?${params.toString()}`;
      const res = await fetch(url, { method: "GET", cache: "no-store", headers: { "Cache-Control": "no-cache" } });
      const text = await res.text();
      if (!res.ok) {
        if (res.status === 404) {
          throw Object.assign(new Error("Not found"), { response: { status: 404 } });
        }
        throw new Error(`Status fetch failed (${res.status})`);
      }
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (parseErr) {
        throw new Error("Invalid JSON response from status endpoint");
      }
      setResult(data || {});
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(t("noctis.status.not_found", { defaultValue: "Geen status gevonden voor deze referentie." }));
        setResult(null);
      } else {
        setError(err?.message || t("noctis.status.fetch_failed", { defaultValue: "Status ophalen mislukt" }));
        setResult(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const detailRows = [
    {
      label: t("noctis.status.fields.reference", { defaultValue: "Referentie" }),
      value: referenceId.trim() || "—",
    },
    {
      label: t("noctis.status.fields.type", { defaultValue: "Type verzoek" }),
      value: result?.requestType || "—",
    },
    {
      label: t("noctis.status.fields.status", { defaultValue: "Status" }),
      value: statusLabel(result?.status, t),
    },
    {
      label: t("noctis.status.fields.submitted", { defaultValue: "Datum ingediend" }),
      value: formatDate(result?.submittedAt),
    },
    {
      label: t("noctis.status.fields.completed", { defaultValue: "Datum afgerond" }),
      value: formatDate(result?.completedAt),
    },
    {
      label: t("noctis.status.fields.note", { defaultValue: "Opmerking" }),
      value: result?.note || t("noctis.status.default_note", {
        defaultValue: "Gegevens worden verwerkt conform AVG.",
      }),
    },
  ];

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FiSearch}
        eyebrow={t("noctis.menu", { defaultValue: "Noctis" })}
        title={t("noctis.status.title", { defaultValue: "Referentie status" })}
        description={t("noctis.status.helper", {
          defaultValue: "Voer de referentie in (bijv. AVG-2025-00421) om de actuele status op te halen.",
        })}
        actions={<NoctisStatusDot />}
      />
      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-semibold text-indigo-900 dark:text-indigo-100">
              {t("noctis.status.subtitle", { defaultValue: "Zoek de status van een Noctis referentie" })}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t("noctis.status.helper", {
                defaultValue: "Voer de referentie in (bijv. AVG-2025-00421) om de actuele status op te halen.",
              })}
            </p>
          </div>

          <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={handleSearch}>
            <input
              type="search"
              value={referenceId}
              onChange={(e) => {
                setReferenceId(e.target.value);
                if (!e.target.value.trim()) setResult(null);
              }}
              placeholder={t("noctis.status.placeholder", { defaultValue: "Zoek op referentie, bijv. AVG-2025-00421" })}
              className="flex-1 rounded-xl border border-indigo-100/70 bg-white/90 px-4 py-2 text-gray-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-200/70 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-white"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 dark:shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            >
              <FiSearch />
              {loading
                ? t("common.loading", { defaultValue: "Loading..." })
                : t("noctis.status.search", { defaultValue: "Zoeken" })}
            </button>
          </form>

          {error && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-100">
              <FiAlertTriangle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {hasResult && (
          <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="mb-4 flex items-center gap-2 text-emerald-700 dark:text-emerald-200">
              <FiCheckCircle />
              <h3 className="text-lg font-semibold">
                {t("noctis.status.result_title", { defaultValue: "Status gevonden" })}
              </h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {detailRows.map((row) => (
                <div
                  key={row.label}
                  className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20"
                >
                  <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
                    {row.label}
                  </p>
                  <p className="mt-1 font-semibold text-gray-900 dark:text-white">{row.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </LdsOverviewPage>
  );
};

export default NoctisReferenceStatus;
